import React from "react";
import {NavLink, useNavigate} from "react-router-dom";
import moment from "moment";
import {motion} from "framer-motion";
import createDOMPurify from "dompurify";
import lodash from "lodash";
import {Splide, SplideSlide} from "@splidejs/react-splide";
import Skeleton from "react-loading-skeleton";
import Cookies from "js-cookie";
import {signal, useSignalEffect} from "@preact/signals-react";

import useSpectaclesStore from "../../../store/public/spectaclesStore";

import SearchFilter from "../../../components/search_filter/search.filter.component";
import Breadcrumbs from "../../../components/breadcrumbs/breadcrumbs.component";
import Pagination from "../../../components/pagination/pagination.component";

import commonStyles from "../common.module.scss";
import noPhoto from "../../../images/no_photo_spectacle.png";

const startIndex = signal(0);
const pageIndex = signal(1);
const pageCount = signal(0);
const filtered = signal([]);

const SpectaclesPage = () => {
    const DOMPurify = createDOMPurify(window);

    const spectaclesStore = useSpectaclesStore();

    const fetchData = async () => {
        await spectaclesStore.loadLastSpectaclesWithTickets();
        await spectaclesStore.loadSpectacles();
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const SpectaclesInThisMonthWithTickets = () => {
        return (
            <>
                {spectaclesStore.lastSpectaclesWithTickets.length > 0 && (
                    <section className={`${commonStyles.wrap} ${commonStyles.wrap_bubble}`}>
                        <h2 className={`${commonStyles.title} ${commonStyles.title_underline}`}>
                            спектакли в этом месяце
                            {/*<span className={commonStyles.titleIcon}>*/}
                            {/*    {AdminIcons.arrow_right_thin}*/}
                            {/*</span>*/}
                        </h2>
                        {spectaclesStore.loading && (
                            <>
                                <Splide
                                    className='my-splide'
                                    options={{
                                        type: "loop",
                                        arrows: false,
                                        perPage: 1,
                                        perMove: 1,
                                        gap: "1.25em",
                                        rewind: true,
                                        pauseOnHover: true,
                                        autoplay: true,
                                        mediaQuery: "min",
                                        breakpoints: {
                                            576: {
                                                perPage: 2,
                                            },
                                            768: {
                                                perPage: 3,
                                            },
                                            1200: {
                                                perPage: 5,
                                            },
                                        },
                                    }}
                                >
                                    {spectaclesStore.lastSpectaclesWithTickets.map((item, index) => (
                                        <SplideSlide data-splide-interval='5000' key={index}>
                                            <div className={commonStyles.posterCard_topList}>
                                                <NavLink
                                                    key={item.ID}
                                                    to={"/spectacles/" + item.ID}
                                                    className={commonStyles.posterCardImgLink}
                                                >
                                                    <Skeleton className={commonStyles.posterCardImg}/>
                                                    <span className={commonStyles.posterCardButton}>Подробнее</span>
                                                </NavLink>
                                                <div className={commonStyles.posterCardMainText}>
                                                    <Skeleton className={commonStyles.posterCardTitle}/>
                                                    <Skeleton className={commonStyles.posterCardTitleSpan}/>
                                                </div>
                                            </div>
                                        </SplideSlide>
                                    ))}
                                </Splide>
                            </>
                        )}
                        {!spectaclesStore.loading && (
                            <>
                                <Splide
                                    className='my-splide'
                                    options={{
                                        type: spectaclesStore.lastSpectaclesWithTickets.length > 5 ? "loop" : "slide",
                                        arrows: false,
                                        perPage: 1,
                                        perMove: 1,
                                        gap: "1.25em",
                                        rewind: true,
                                        pauseOnHover: true,
                                        autoplay: true,
                                        mediaQuery: "min",
                                        breakpoints: {
                                            576: {
                                                perPage: 2,
                                            },
                                            768: {
                                                perPage: 3,
                                            },
                                            1200: {
                                                perPage: 5,
                                            },
                                        },
                                    }}
                                >
                                    {spectaclesStore.lastSpectaclesWithTickets.map((item, index) => (
                                        <SplideSlide data-splide-interval='5000' key={index}>
                                            <div className={commonStyles.posterCard_topList}>
                                                <NavLink
                                                    key={item.ID}
                                                    to={"/spectacles/" + item.ID}
                                                    className={commonStyles.posterCardImgLink}
                                                >
                                                    <img
                                                        className={commonStyles.posterCardImg}
                                                        src={
                                                            item.poster === null
                                                                ? noPhoto
                                                                : process.env.REACT_APP_BASE_URL + item.poster
                                                        }
                                                        alt={item.title}
                                                    />
                                                    <span className={commonStyles.posterCardButton}>Подробнее</span>
                                                </NavLink>
                                                <div className={commonStyles.posterCardMainText}>
                                                    <h3 className={commonStyles.posterCardTitle}>
                                                        {item.title}
                                                        <span className={commonStyles.posterCardTitleSpan}>
                                                            {item.schoolTitle}
                                                        </span>
                                                    </h3>
                                                </div>
                                            </div>
                                        </SplideSlide>
                                    ))}
                                </Splide>
                            </>
                        )}
                    </section>
                )}
            </>
        );
    };

    const Spectacles = () => {
        const pageSize = 12;
        const [paginatedItems, setPaginatedItems] = React.useState([]);
        const itemConfig = [
            {
                header: "Школа",
                theme: "public",
                size: "normal",
                key: "schoolTitle",
                type: "string",
                filter: "string",
                sorting: true,
            },
            {
                header: "Возраст участников",
                theme: "public",
                size: "normal",
                key: "age_members",
                type: "array",
                filter: "select",
                sorting: false,
            },
            {
                header: "Округ",
                theme: "public",
                size: "normal",
                key: "okrug",
                type: "string",
                filter: "select",
                sorting: true,
            },
            {
                header: "Театр",
                theme: "public",
                size: "normal",
                key: "theatreTitle",
                type: "string",
                filter: "string",
                sorting: true,
            },
            {
                header: "Спектакль",
                theme: "public",
                size: "normal",
                key: "title",
                type: "string",
                filter: "string",
                sorting: true,
            },
        ];

        useSignalEffect(() => {
            pageCount.value = Math.ceil(filtered.value.length / pageSize);

            setPaginatedItems(lodash(filtered.value)
                .slice(startIndex.value)
                .take(pageSize)
                .value());
        });

        React.useEffect(() => {
            if (Cookies.get("table_public_spectacles")) {
                handleChangePage(parseInt(Cookies.get("table_public_spectacles")));
            } else {
                handleChangePage(1);
            }
        }, []);

        const handleFilter = (filter, changePage = true) => {
            function checkItem(config, itemValue, filterValue, prop) {
                if (prop === "search_string") {
                    let tmpFilter = {};

                    for (const itemKey in itemValue)
                        if (itemValue[itemKey] !== null)
                            tmpFilter[itemKey] = !!itemValue[itemKey]
                                .toString()
                                .toLowerCase()
                                .includes(filterValue["search_string"].toLowerCase());

                    if (Object.keys(tmpFilter).some((key) => tmpFilter[key])) return true;
                }

                switch (config?.type) {
                    case "int":
                        return parseInt(itemValue[prop]) === parseInt(filterValue[prop]);

                    case "string":
                        return config.filter === "select" || config.filter === "multiselect"
                            ? itemValue[prop] === filterValue[prop]
                            : itemValue[prop].toLowerCase().includes(filterValue[prop].toLowerCase());

                    case "date":
                        if ("linkKey" in config) {
                            if ("dateFilter" in config && config.dateFilter === "to")
                                return moment(itemValue[config["linkKey"]]).isBefore(moment(filterValue[prop]));
                            if ("dateFilter" in config && config.dateFilter === "from")
                                return moment(itemValue[config["linkKey"]]).isAfter(moment(filterValue[prop]));
                        }

                        const date = moment(itemValue[prop]);

                        return moment(
                            `${date.year()}-${
                                date.month() <= 9 ? "0" + (date.month() + 1) : date.month() + 1
                            }-${date.date()}`
                        ).isSame(moment(filterValue[prop]));

                    case "datetime":
                        if ("linkKey" in config) {
                            if ("dateFilter" in config && config.dateFilter === "to")
                                return moment(itemValue[config["linkKey"]]).isBefore(moment(filterValue[prop]));
                            if ("dateFilter" in config && config.dateFilter === "from")
                                return moment(itemValue[config["linkKey"]]).isAfter(moment(filterValue[prop]));
                        }

                        const itemDate = moment(itemValue[prop]);
                        return moment({
                            year: itemDate.get("year"),
                            month: itemDate.get("month"),
                            day: itemDate.get("date"),
                        }).isSame(moment(filterValue[prop]));

                    case "array":
                        return itemValue[prop].some((item) => item === filterValue[prop]);

                    default:
                        if (itemValue[prop]) return itemValue[prop] === filterValue[prop];
                        else return false;
                }
            }

            if (filter) {
                let tmpArray = [];

                for (const item of spectaclesStore.spectacles) {
                    let tmpFilter = {};

                    for (let prop in filter)
                        tmpFilter[prop] = !!(
                            filter[prop] === "" ||
                            filter[prop] === "Все" ||
                            checkItem(
                                itemConfig.find((itemConfig) => itemConfig.key === prop),
                                item,
                                filter,
                                prop
                            )
                        );

                    if (!Object.keys(tmpFilter).some((key) => !tmpFilter[key])) {
                        tmpArray.push(item);
                    }
                }

                filtered.value = tmpArray;
            } else
                filtered.value = spectaclesStore.spectacles;

            if (changePage)
                handleChangePage(1);
        };

        const handleChangePage = (index) => {
            startIndex.value = (index - 1) * pageSize;
            pageIndex.value = index;

            Cookies.set("table_public_spectacles", index);
        };

        return (
            <section className={`${commonStyles.wrap} ${commonStyles.wrap_shadow}`}>
                <h2 className={`${commonStyles.title} ${commonStyles.title_underline}`}>репертуар школьных театров</h2>
                {spectaclesStore.loading && (
                    <>
                        <Skeleton height={66}/>
                        <Skeleton width={"25vmax"} height={40} style={{margin: "1.875em auto", display: "block"}}/>
                        <ul className={commonStyles.cardDeck}>
                            {Array(4)
                                .fill()
                                .map((item, index) => (
                                    <li className={commonStyles.posterCard} key={index}>
                                        <div className={commonStyles.posterCardImgLink}>
                                            <Skeleton className={commonStyles.posterCardImg}/>
                                        </div>
                                        <div className={commonStyles.posterCardMainText}>
                                            <Skeleton className={commonStyles.posterCardTitle}/>
                                            <Skeleton
                                                className={commonStyles.posterCardAccent}
                                                count={3}
                                                width={"20vmax"}
                                            />
                                            <Skeleton className={commonStyles.posterCardDescription} count={3}/>
                                            <div className={commonStyles.posterCardFooter}>
                                                <Skeleton width={"7em"} height={"2em"}/>
                                                <Skeleton className={commonStyles.posterInfoText} width={"10em"}/>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                        </ul>
                        <Skeleton width={"25vmax"} height={40} style={{margin: "1.875em auto", display: "block"}}/>
                    </>
                )}
                {!spectaclesStore.loading && (
                    <>
                        <SearchFilter
                            front={true}
                            items={spectaclesStore.spectacles}
                            config={itemConfig}
                            onSubmit={handleFilter}
                        />
                        <Pagination pageCount={pageCount.value} pageIndex={pageIndex.value}
                                    setPageChangeCallback={handleChangePage}/>
                        <ul className={commonStyles.cardDeck}>
                            {paginatedItems.map((item, index) => (
                                <li key={window.global.makeid(12)} className={[commonStyles.posterCard].join(" ")}>
                                    <NavLink
                                        key={item.ID}
                                        to={"/spectacles/" + item.ID}
                                        className={commonStyles.posterCardImgLink}
                                    >
                                        <img
                                            className={commonStyles.posterCardImg}
                                            src={
                                                item.poster === null
                                                    ? noPhoto
                                                    : process.env.REACT_APP_BASE_URL + item.poster
                                            }
                                            alt={item.title}
                                        />
                                    </NavLink>
                                    <div className={commonStyles.posterCardMainText}>
                                        <h3 className={commonStyles.posterCardTitle}>{item.title}</h3>
                                        <ul className={commonStyles.posterCardList}>
                                            <li className={commonStyles.posterCardItem}>
                                                Организация:
                                                <span className={commonStyles.posterCardAccent}>
                                                    {item.schoolTitle}
                                                </span>
                                            </li>
                                            <li className={commonStyles.posterCardItem}>
                                                Театр:
                                                <span className={commonStyles.posterCardAccent}>
                                                    <NavLink
                                                        className={commonStyles.link}
                                                        to={"/theatres/" + item.theatreID}
                                                    >
                                                        {item.theatreTitle}
                                                    </NavLink>
                                                </span>
                                            </li>
                                            <li className={commonStyles.posterCardItem + " --hide"}>
                                                Дата показа:
                                                <span className={commonStyles.posterCardAccent}>{item.date}</span>
                                            </li>
                                        </ul>
                                        <div
                                            className={commonStyles.posterCardDescription}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(
                                                    window.global.truncateString(item.review, 300)
                                                ),
                                            }}
                                        />
                                        <div className={commonStyles.posterCardFooter}>
                                            <NavLink
                                                key={item.ID}
                                                to={"/spectacles/" + item.ID}
                                                className={commonStyles.posterCardButton}
                                            >
                                                Подробнее
                                            </NavLink>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <Pagination pageCount={pageCount.value} pageIndex={pageIndex.value}
                                    setPageChangeCallback={handleChangePage}/>
                    </>
                )}
            </section>
        );
    };

    return (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.5}}
        >
            <Breadcrumbs
                items={[
                    {
                        title: "Главная",
                        url: "/",
                    },
                    {
                        title: "Афиши",
                        url: "",
                    },
                ]}
            />
            <SpectaclesInThisMonthWithTickets/>
            <Spectacles/>
        </motion.div>
    );
};

export default SpectaclesPage;
